import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Upload, Undo2, Download, ArrowRight } from "lucide-react";
import { api, downloadBlob, errMsg } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const n = (v) => Number(v || 0).toLocaleString("en-IN");
const plural = (v, one, many) => (Number(v) === 1 ? one : many);

const STATUS = {
  assign: { label: "Will assign", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  overwrite: { label: "Will replace", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  unchanged: { label: "Unchanged", cls: "bg-muted text-muted-foreground border-border" },
  skipped: { label: "Skipped", cls: "bg-muted text-muted-foreground border-border" },
  error: { label: "Error", cls: "bg-amber-100 text-amber-800 border-amber-200" },
};

/** Phase 186 — the "Download List" Excel (every property without a mohalla, all wards) with the Correct Mohalla column. */
export async function downloadStrandedList(code) {
  const stamp = new Date().toISOString().slice(0, 10);
  await downloadBlob(`/t/${code}/mohalla-backfill/stranded/export`, `properties-without-mohalla_${code}_${stamp}.xlsx`);
}

/**
 * Phase 186 — Bulk Upload of mohalla corrections (Coverage Alerts → "Bulk Upload").
 * Step 1: pick the completed "Download List" file → Preview (nothing written).
 * Step 2: review assign / replace / unchanged / skipped / error rows → Apply → result with Undo.
 * Applied as a Mohalla Backfill run (shows in the Backfill history, undo-able); assignments re-sync automatically.
 */
export function MohallaUploadDialog({ code, open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null); setPreview(null); setResult(null); setError(null); setFilter("all"); setBusy(false);
  }, [open]);

  const choose = (f) => { setFile(f || null); setPreview(null); setResult(null); setError(null); };

  const runPreview = async () => {
    if (!file) return toast.error("Choose the completed 'Download List' file first");
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/t/${code}/mohalla-backfill/upload/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setFilter(r.data.summary?.error > 0 ? "error" : "all");
      const s = r.data.summary || {};
      if (s.changes === 0) toast.warning("No row has a valid Correct Mohalla — nothing to apply", { description: s.error ? `${n(s.error)} ${plural(s.error, "row needs", "rows need")} a fix` : undefined });
      else if (s.error > 0) toast.warning(`${n(s.changes)} ${plural(s.changes, "property", "properties")} ready · ${n(s.error)} ${plural(s.error, "row has", "rows have")} errors and will be left out`);
      else toast.success(`${n(s.changes)} ${plural(s.changes, "property", "properties")} ready — review and apply`);
    } catch (e) {
      setError(errMsg(e));
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview?.plan_id) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/mohalla-backfill/upload/${preview.plan_id}/apply`);
      setResult(r.data);
      toast.success(`${n(r.data.filled)} ${plural(r.data.filled, "property", "properties")} updated`, {
        description: r.data.sync?.moved || r.data.sync?.assigned
          ? `Assignments updated: ${n(r.data.sync.assigned)} assigned, ${n(r.data.sync.moved)} moved to the collector who now covers them`
          : "Collector assignments already matched the new mohallas",
      });
      onDone?.(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const undo = async () => {
    if (!result?.run_id) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/mohalla-backfill/runs/${result.run_id}/undo`);
      toast.success(`Upload undone — ${n(r.data.reverted)} ${plural(r.data.reverted, "property", "properties")} restored`);
      setResult({ ...result, undone: true, reverted: r.data.reverted });
      onDone?.(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const s = preview?.summary || {};
  const rows = preview?.rows || [];
  const shown = filter === "all" ? rows : rows.filter((r) => (filter === "changes" ? r.status === "assign" || r.status === "overwrite" : r.status === filter));
  const canApply = !!preview && (s.changes || 0) > 0 && !busy && !result;

  const chip = (key, label, value, cls, active) => (
    <button type="button" key={key} onClick={() => setFilter(active ? "all" : key)} aria-pressed={active}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${cls} ${active ? "ring-2 ring-ring" : ""}`}
      data-testid={`mohalla-upload-chip-${key}`}>
      <span className="font-semibold">{n(value)}</span> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mohalla-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base"><Upload className="h-4 w-4" /> Bulk Upload — mohalla corrections</DialogTitle>
          <DialogDescription>
            Upload the <b>Download List</b> file with the <b>Correct Mohalla</b> column filled in. Rows are matched by Property PIN; the mohalla
            name must exist under that property's ward in Master Data (see the "Mohalla reference" sheet). Nothing is written until you apply;
            the upload can be undone afterwards and collector assignments re-sync automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* step 1 — file */}
          <div className="rounded-lg border p-3 space-y-2" data-testid="mohalla-upload-step-file">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[240px] space-y-1.5">
                <Label htmlFor="mohalla-upload-file">Completed file (.xlsx or .csv)</Label>
                <Input id="mohalla-upload-file" ref={inputRef} type="file" accept=".xlsx,.xls,.csv" disabled={busy || !!result}
                  onChange={(e) => choose(e.target.files?.[0])} data-testid="mohalla-upload-file-input" />
              </div>
              <Button onClick={runPreview} disabled={!file || busy || !!result} data-testid="mohalla-upload-preview-button">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> {busy && !preview ? "Checking…" : "Preview"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Don't have the file yet?{" "}
              <button type="button" className="underline underline-offset-2 hover:text-foreground" data-testid="mohalla-upload-download-link"
                onClick={() => downloadStrandedList(code).then(() => toast.success("List downloaded")).catch((e) => toast.error(errMsg(e)))}>
                <Download className="inline h-3 w-3 mr-0.5 -mt-0.5" />Download List
              </button>{" "}
              — every property without a mohalla, with a blank Correct Mohalla column to fill.
            </p>
            {error && <p className="text-sm text-destructive" data-testid="mohalla-upload-error">{error}</p>}
          </div>

          {/* step 2 — preview */}
          {preview && (
            <div className="space-y-2" data-testid="mohalla-upload-preview">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">Preview · {preview.filename}</p>
                <Badge variant={s.changes ? "default" : "outline"} data-testid="mohalla-upload-changes-badge">
                  {n(s.changes)} of {n(s.total)} {plural(s.total, "row", "rows")} will be applied
                </Badge>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="mohalla-upload-summary">
                {chip("assign", plural(s.assign, "to assign", "to assign"), s.assign, STATUS.assign.cls, filter === "assign")}
                {s.overwrite > 0 && chip("overwrite", "to replace", s.overwrite, STATUS.overwrite.cls, filter === "overwrite")}
                {s.unchanged > 0 && chip("unchanged", "unchanged", s.unchanged, STATUS.unchanged.cls, filter === "unchanged")}
                {s.skipped > 0 && chip("skipped", "left blank (skipped)", s.skipped, STATUS.skipped.cls, filter === "skipped")}
                {chip("error", plural(s.error, "error", "errors"), s.error, s.error ? STATUS.error.cls : STATUS.unchanged.cls, filter === "error")}
                {filter !== "all" && (
                  <button type="button" className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground" onClick={() => setFilter("all")} data-testid="mohalla-upload-filter-all">Show all</button>
                )}
              </div>
              {s.error > 0 && !result && (
                <p className="text-xs text-amber-800 flex items-start gap-1.5" data-testid="mohalla-upload-error-note">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Error rows are left out — nothing is written for them. Fix the names in the file (the "Mohalla reference" sheet lists valid names per ward) and upload again, or apply the valid rows now.
                </p>
              )}
              <div className="rounded-md border overflow-hidden">
                <div className="max-h-72 overflow-auto">
                  <Table data-testid="mohalla-upload-rows">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>PIN</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Ward</TableHead>
                        <TableHead>Mohalla</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shown.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-6">No rows in this group.</TableCell></TableRow>
                      )}
                      {shown.map((r) => {
                        const st = STATUS[r.status] || STATUS.skipped;
                        return (
                          <TableRow key={r.row} className={r.status === "error" ? "bg-amber-50/60 dark:bg-amber-950/10" : ""} data-testid={`mohalla-upload-row-${r.row}`} data-status={r.status}>
                            <TableCell className="text-xs text-muted-foreground tabular-nums">{r.row}</TableCell>
                            <TableCell className="font-mono text-xs">{r.pin || "—"}</TableCell>
                            <TableCell className="text-xs">{r.owner_name || "—"}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{r.ward_name || "—"}{r.zone_name ? <span className="text-muted-foreground"> · {r.zone_name}</span> : null}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {r.status === "overwrite" ? (
                                <span className="inline-flex items-center gap-1"><span className="line-through text-muted-foreground">{r.current_mohalla || "—"}</span><ArrowRight className="h-3 w-3" />{r.correct}</span>
                              ) : r.status === "assign" ? (
                                <span className="inline-flex items-center gap-1"><span className="text-muted-foreground">blank</span><ArrowRight className="h-3 w-3" />{r.correct}</span>
                              ) : (r.correct || r.current_mohalla || "—")}
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="flex flex-col gap-0.5 items-start">
                                <Badge variant="outline" className={`text-[11px] ${st.cls}`}>{st.label}</Badge>
                                {r.message && (r.status === "error" || r.status === "skipped") && <span className="text-[11px] text-muted-foreground max-w-xs" data-testid={`mohalla-upload-message-${r.row}`}>{r.message}</span>}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {preview.rows_shown < s.total && (
                  <p className="px-3 py-1.5 text-[11px] text-muted-foreground bg-muted/30">Showing the first {n(preview.rows_shown)} of {n(s.total)} rows (errors and changes first). All rows are included when you apply.</p>
                )}
              </div>
            </div>
          )}

          {/* result */}
          {result && (
            <div className={`rounded-lg border p-3 space-y-1 text-sm ${result.undone ? "border-border bg-muted/30" : "border-emerald-500/30 bg-emerald-500/5"}`} data-testid="mohalla-upload-result">
              {result.undone ? (
                <p className="font-medium flex items-center gap-1.5"><Undo2 className="h-4 w-4" /> Upload undone — {n(result.reverted)} {plural(result.reverted, "property", "properties")} restored</p>
              ) : (
                <>
                  <p className="font-medium flex items-center gap-1.5 text-emerald-800 dark:text-emerald-300" data-testid="mohalla-upload-result-title">
                    <CheckCircle2 className="h-4 w-4" /> {n(result.filled)} {plural(result.filled, "property", "properties")} updated
                    <span className="text-xs font-normal text-muted-foreground">({n(result.assigned)} assigned, {n(result.overwritten)} replaced) across {n(result.wards)} {plural(result.wards, "ward", "wards")}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Assignments: {n(result.sync?.assigned)} newly assigned, {n(result.sync?.moved)} moved to the collector who now covers them, {n((result.sync?.no_collector || 0) + (result.sync?.uncovered || 0))} still without a covering collector.
                    {result.errors > 0 ? ` ${n(result.errors)} error ${plural(result.errors, "row was", "rows were")} left out.` : ""}
                    {result.collectors?.length ? ` Gained: ${result.collectors.slice(0, 4).map((c) => `${c.name} +${n(c.assigned)}`).join(", ")}.` : ""}
                  </p>
                  <Button size="sm" variant="outline" className="h-7 text-xs mt-1" onClick={undo} disabled={busy} data-testid="mohalla-upload-undo-button">
                    <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo this upload
                  </Button>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mohalla-upload-close-button">{result ? "Done" : "Cancel"}</Button>
            {!result && (
              <Button onClick={apply} disabled={!canApply} data-testid="mohalla-upload-apply-button">
                <Upload className="h-4 w-4 mr-2" /> {busy && preview ? "Applying…" : `Apply ${s.changes ? n(s.changes) : ""} ${plural(s.changes, "change", "changes")}`}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
